/**
 * Putting the AGG-checked feedback paragraph into the rejection email, and taking it out again.
 *
 * The Archive modal edits the email in ONE contentEditable field, so there is no React state
 * holding the body to splice a paragraph into — the text lives in the DOM (see EmailBodyEditor:
 * re-rendering it from state fights the caret). These helpers therefore work on the editor element
 * directly.
 *
 * ── Why a marker element ─────────────────────────────────────────────────────────────────────
 * "One click removes it again" has to survive the recruiter editing around the inserted paragraph,
 * so removal cannot mean "find that text and delete it". The block goes in wrapped in a
 * `<span data-agg-feedback>`, which makes removal one `querySelector`. The wrapper is unwrapped
 * again by `stripFeedbackMarkers` before the body is read for sending, so nothing candidate-facing
 * carries an internal marker — the sanitizer would drop the tag anyway, but the email that goes out
 * should not depend on that.
 *
 * ── Where it goes ────────────────────────────────────────────────────────────────────────────
 * The three "rejection in later stages" templates are the same email apart from ONE paragraph: the
 * Tech-case one writes the feedback out, the other two offer a call or invite the candidate to ask.
 * So the block is inserted directly BEFORE that paragraph — which is exactly where the Tech-case
 * template already carries it, and it reads in the right order: here is the feedback, and here is
 * how to get more of it.
 *
 * A template whose wording no longer mentions feedback (e.g. "Rejection — after first / pre-screen")
 * anchors instead just before the "stay in touch" paragraph — the one linking to LinkedIn and the
 * careers page — so the feedback sits between the "positive impression" paragraph and "stay in
 * touch", never after the sign-off and never in the voucher P.S. (TA, Oct 2026). A template that has
 * neither a feedback nor a stay-in-touch paragraph falls back to "just before the sign-off", which
 * is the last place it can go without landing after the greeting.
 *
 * ── …and what it replaces ────────────────────────────────────────────────────────────────────
 * On the first-interview variant that paragraph does not survive the insertion: "if you would like
 * more detailed feedback, feel free to reach out – we would be happy to share a few additional
 * insights with you by email" contradicts an email that now contains those insights (TA feedback,
 * Anna Thiel, 16 Sep 2026). `replacesOffer` therefore REMOVES it and stashes it on the marker, so
 * the result reads like the Tech-case template — feedback instead of an invitation to ask for it —
 * and one click still puts the original wording back.
 *
 * The caller decides, from the department × stage rule (`contentFor`), never this module: the
 * feedback-CALL variant keeps its paragraph, because a 15-minute call is a channel the written
 * paragraph does not replace and its booking link is the only thing offering it.
 *
 * Paragraphs are `<br><br>`-separated runs, not elements — that is the HTML shape
 * `markdownEmailToBrHtml` produces server-side and the shape the sanitizer allows.
 */

export const MARKER_ATTR = 'data-agg-feedback';

/**
 * The replaced offer paragraph, stashed as HTML on the marker element.
 *
 * On the marker and nowhere else, deliberately: `stripFeedbackMarkers` unwraps that element before
 * the body is read for sending, which drops the attribute with it, so the stashed wording can never
 * reach a candidate — and it travels with the DOM, so a language switch (which replaces the whole
 * body) cannot leave a stale German paragraph waiting to be restored into an English email.
 */
export const REPLACED_ATTR = 'data-agg-replaced';

/**
 * The stash is percent-encoded, not stored as raw markup.
 *
 * HTML attribute values escape `&` and `"` but NOT `<`, so a paragraph parked verbatim would put
 * `<br><br>` inside an attribute of the body's own HTML — valid, and re-parsed correctly, but every
 * regex that has ever walked this markup (the sanitizer's allowlist, the plain-text conversion,
 * this file's own tests) would then see tags that are not there. Encoding keeps the attribute to
 * characters no tag-shaped pattern can match.
 */
const encodeStash = (html: string) => encodeURIComponent(html);
const decodeStash = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    // A recruiter's editing can only lose the attribute, never rewrite it — but a half-copied one
    // must not throw where a click is expected: the block then simply comes out without a restore.
    return '';
  }
};

interface Paragraph {
  /** First node of the paragraph — the insertion anchor. */
  start: ChildNode;
  text: string;
  /** Every node the paragraph owns, including the blank line that ends it. */
  nodes: ChildNode[];
}

/**
 * Where the paragraphs actually live.
 *
 * The server wraps every rejection body in one `<div style="line-height: 1.5;">`, so the editor's
 * only child is that wrapper and the `<br><br>` runs are one level down. Walking the editor itself
 * would see a single "paragraph" and put the block in the wrong place — after the whole email.
 */
function contentRoot(editor: HTMLElement): HTMLElement {
  let root: HTMLElement = editor;
  for (;;) {
    const kids = Array.from(root.childNodes).filter(
      (n) => !(n.nodeType === Node.TEXT_NODE && !n.textContent?.trim())
    );
    const only = kids.length === 1 ? kids[0] : null;
    if (only && only.nodeName === 'DIV') {
      root = only as HTMLElement;
      continue;
    }
    return root;
  }
}

/**
 * The body's paragraphs, split on runs of two or more `<br>`.
 *
 * Each paragraph also carries the nodes it is MADE of, ending with the blank line that closes it —
 * that is what lets a paragraph be removed whole, blank line and all, rather than leaving the gap
 * it used to fill behind.
 */
function paragraphs(editor: HTMLElement): Paragraph[] {
  const out: Paragraph[] = [];
  let current: Paragraph | null = null;
  /** `<br>`s and layout whitespace seen since the last content node — owned by whatever follows. */
  let pending: ChildNode[] = [];
  let breaks = 0;

  for (const node of Array.from(editor.childNodes)) {
    if (node.nodeName === 'BR') {
      breaks += 1;
      pending.push(node);
      continue;
    }
    // Whitespace between tags is layout, not content: it must not end a paragraph, and it must not
    // become the anchor either — contentEditable sprinkles such nodes around freely.
    if (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()) {
      pending.push(node);
      continue;
    }

    // A blank line closes the paragraph it follows, so the separator belongs to THAT paragraph;
    // a single <br> is a line break inside one, and stays where it is.
    if (current) current.nodes.push(...pending);
    if (breaks >= 2) current = null;
    pending = [];
    breaks = 0;
    if (!current) {
      current = { start: node, text: '', nodes: [] };
      out.push(current);
    }
    current.nodes.push(node);
    current.text += node.textContent ?? '';
  }
  if (current) current.nodes.push(...pending);
  return out;
}

/**
 * The editor HTML for a server draft whose feedback paragraph IS the decision log's text.
 *
 * The proxy writes that paragraph into the first draft as plain text; wrapping it (and the blank
 * line after it) in the same marker the button inserts makes "− Remove AGG feedback" take it out
 * cleanly and `stripFeedbackMarkers` unwrap it on send. A draft that does not contain the prose
 * (an older proxy, an edited draft) is rendered unmarked.
 */
export function draftHtmlWithMarkedFeedback(
  draft: string,
  prose: string | undefined,
  toHtml: (text: string) => string
): string {
  const start = prose ? draft.indexOf(prose) : -1;
  if (!prose || start < 0) return toHtml(draft);
  let end = start + prose.length;
  if (draft.startsWith('\n\n', end)) end += 2;
  return (
    toHtml(draft.slice(0, start)) +
    `<span ${MARKER_ATTR}="1">${toHtml(draft.slice(start, end))}</span>` +
    toHtml(draft.slice(end))
  );
}

/** Is the AGG paragraph currently in the email? */
export function hasFeedbackBlock(editor: HTMLElement): boolean {
  return Boolean(editor.querySelector(`[${MARKER_ATTR}]`));
}

/**
 * A trailing "P.S." paragraph — the €200-voucher postscript the server appends AFTER the signature
 * on every rejection email (`VOUCHER_PS_*` in `lib/emailTemplates.js`). The fallback anchor is the
 * sign-off, so it has to step over this; otherwise, on a template that carries no feedback paragraph
 * to anchor to (e.g. "Rejection — after first / pre-screen"), the block lands between the signature
 * and the voucher instead of before the sign-off (reported Oct 2026).
 */
function isPostscript(text: string): boolean {
  return /^\s*p\.?\s*s[.:]/i.test(text);
}

/**
 * The "stay in touch" paragraph, identified by its LinkedIn link. Every rejection template words
 * this paragraph differently and in two languages, but all of them link to the FINN LinkedIn page
 * (`VOUCHER`/careers aside), so the href is the one stable signal across wordings — matching on the
 * anchor TEXT ("LinkedIn") would break the day a template localises the label. This is the anchor
 * for templates that carry no feedback paragraph, so the block lands before it rather than at the
 * sign-off.
 */
const STAY_IN_TOUCH_LINK = /linkedin\.com/i;

/** The markup of a run of nodes, read without disturbing them. */
function serialize(nodes: ChildNode[], doc: Document): string {
  const holder = doc.createElement('div');
  nodes.forEach((n) => holder.appendChild(n.cloneNode(true)));
  return holder.innerHTML;
}

/**
 * Insert the paragraph. Returns false when there is nothing to insert or it is already there, so
 * the caller never flips its button into a state the body does not match.
 *
 * `replacesOffer` additionally removes the feedback paragraph the block was anchored to — only
 * when one was actually found, never the sign-off the fallback lands before.
 */
export function insertFeedbackBlock(
  editor: HTMLElement,
  blockHtml: string,
  { replacesOffer = false }: { replacesOffer?: boolean } = {}
): boolean {
  if (!blockHtml.trim() || hasFeedbackBlock(editor)) return false;

  const marker = editor.ownerDocument.createElement('span');
  marker.setAttribute(MARKER_ATTR, '1');

  const root = contentRoot(editor);
  const paras = paragraphs(root);
  // Never the first paragraph: that is the greeting, and feedback does not go above "Hi Aylin,".
  const body = paras.slice(1);
  const offer = body.find((p) => /feedback/i.test(p.text)) ?? null;
  // A template with no feedback paragraph anchors before the "stay in touch" paragraph instead, so
  // the block sits between "positive impression" and "stay in touch" rather than dropping to the
  // sign-off (TA, Oct 2026).
  const stayInTouch =
    body.find((p) => STAY_IN_TOUCH_LINK.test(serialize(p.nodes, editor.ownerDocument))) ?? null;
  // Final fallback: just before the sign-off. The LAST paragraph is not always the sign-off —
  // rejection emails carry a "P.S." voucher appended after the signature — so step back past any
  // trailing postscript, never past the greeting.
  let signOffIdx = paras.length - 1;
  while (signOffIdx > 1 && isPostscript(paras[signOffIdx].text)) signOffIdx -= 1;
  const anchor = offer ?? stayInTouch ?? paras[signOffIdx];

  if (!anchor || paras.length < 2) {
    // A body with no paragraph structure at all (a template edited down to one line): append,
    // rather than refuse — the recruiter can still see it and move it.
    marker.innerHTML = `<br><br>${blockHtml}`;
    root.appendChild(marker);
    return true;
  }

  marker.innerHTML = `${blockHtml}<br><br>`;
  root.insertBefore(marker, anchor.start);

  if (replacesOffer && offer) {
    // Stash before removing: put back is the same click that took the block out.
    marker.setAttribute(REPLACED_ATTR, encodeStash(serialize(offer.nodes, editor.ownerDocument)));
    offer.nodes.forEach((n) => n.remove());
  }
  return true;
}

/**
 * Take it out again — the whole paragraph and the blank line it brought with it, and, where it
 * displaced the "ask us for feedback" paragraph, that paragraph back in its own place.
 */
export function removeFeedbackBlock(editor: HTMLElement): void {
  editor.querySelectorAll(`[${MARKER_ATTR}]`).forEach((el) => {
    const replaced = decodeStash(el.getAttribute(REPLACED_ATTR) ?? '');
    if (replaced) {
      const holder = el.ownerDocument.createElement('div');
      holder.innerHTML = replaced;
      // The marker sits exactly where the offer paragraph began, so this restores the order too.
      el.replaceWith(...Array.from(holder.childNodes));
      return;
    }
    el.remove();
  });
}

/**
 * The body as it should be SENT: same markup, minus the wrapper that made the paragraph removable
 * — and minus the offer paragraph stashed on it, which the recruiter chose to replace.
 * Works on a detached copy, so calling it never disturbs the editor or the caret.
 */
export function stripFeedbackMarkers(html: string): string {
  if (!html.includes(MARKER_ATTR)) return html;
  const holder = document.createElement('div');
  holder.innerHTML = html;
  holder.querySelectorAll(`[${MARKER_ATTR}]`).forEach((el) => {
    el.replaceWith(...Array.from(el.childNodes));
  });
  return holder.innerHTML;
}
